import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Tab,
    Tabs,
    useTheme,
} from '@material-ui/core'
import AssignmentIcon from '@material-ui/icons/Assignment'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Container } from '../../components'
import { vendorProjectApi } from '../../contexts'
import {
    CREATE_PROJECT_MODAL_HEIGHT,
    VendorProjectFormData,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { AiProjectGenerator } from './AiProjectGenerator'
import { ProjectFieldsEditor } from './ProjectFieldsEditor'
import {
    ProjectFieldErrors,
    blankProjectForm,
    validateProjectFields,
} from './projectFormHelpers'

interface Props {
    open: boolean
    onClose: () => void
}

type CreateTab = 'manual' | 'ai'

interface DiscardConfirmState {
    title: string
    message: string
    confirmLabel: string
    onConfirm: () => void
}

const ErrorBanner = ({ message }: { message: string }) => {
    const theme = useTheme()
    return (
        <Container
            alignItems="flex-start"
            style={{
                gap: theme.spacing(1),
                padding: theme.spacing(1.25, 1.5),
                borderRadius: 8,
                border: `1px solid ${theme.palette.error.light}`,
                backgroundColor: '#ffebee',
                color: theme.palette.error.dark,
            }}
        >
            <ErrorOutlineIcon fontSize="small" style={{ marginTop: 2 }} />
            <Container
                direction="column"
                flex={1}
                style={{ ...typographyStyle(theme.typography.body2) }}
            >
                <span style={{ fontWeight: 700 }}>
                    Could not create project
                </span>
                <span>{message}</span>
            </Container>
        </Container>
    )
}

export const CreateProjectModal = ({ open, onClose }: Props) => {
    const theme = useTheme()
    const { createVendorProject } = vendorProjectApi()
    const [tab, setTab] = useState<CreateTab>('manual')
    const [manualForm, setManualForm] = useState<VendorProjectFormData>(
        blankProjectForm(),
    )
    const [manualErrors, setManualErrors] = useState<ProjectFieldErrors>({})
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [aiDirty, setAiDirty] = useState(false)
    const [
        discardConfirm,
        setDiscardConfirm,
    ] = useState<DiscardConfirmState | null>(null)

    useEffect(() => {
        if (!open) return
        setTab('manual')
        setManualForm(blankProjectForm())
        setManualErrors({})
        setSubmitError(null)
        setSubmitting(false)
        setAiDirty(false)
        setDiscardConfirm(null)
    }, [open])

    const requestClose = () => {
        if (tab === 'ai' && aiDirty) {
            setDiscardConfirm({
                title: 'Discard Lori draft?',
                message:
                    'Your current Lori draft will be cleared if you close this dialog.',
                confirmLabel: 'Discard and Close',
                onConfirm: () => {
                    setAiDirty(false)
                    onClose()
                },
            })
            return
        }
        onClose()
    }

    const handleTabChange = (_: unknown, next: CreateTab) => {
        if (tab === 'ai' && aiDirty && next !== 'ai') {
            setDiscardConfirm({
                title: 'Switch to manual create?',
                message:
                    'Switching tabs will discard the current Lori draft and review edits.',
                confirmLabel: 'Discard and Switch',
                onConfirm: () => {
                    setAiDirty(false)
                    setTab(next)
                    setSubmitError(null)
                },
            })
            return
        }
        setTab(next)
        setSubmitError(null)
    }

    const handleManualCreate = async () => {
        const nextErrors = validateProjectFields(manualForm)
        setManualErrors(nextErrors)
        if (Object.keys(nextErrors).length > 0) return

        setSubmitting(true)
        setSubmitError(null)
        try {
            await createVendorProject(manualForm)
            onClose()
        } catch (e) {
            setSubmitError(
                e instanceof Error && e.message
                    ? e.message
                    : 'Something went wrong. Please try again.',
            )
        } finally {
            setSubmitting(false)
        }
    }

    const title = tab === 'manual' ? 'Create Project' : 'Generate with Lori'
    const subtitle =
        tab === 'manual'
            ? 'Set up a new project to track work'
            : 'Let Lori draft a project and task list from a document or description'

    return (
        <>
            <Dialog
                open={open}
                onClose={requestClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: {
                        height: CREATE_PROJECT_MODAL_HEIGHT,
                        maxHeight: CREATE_PROJECT_MODAL_HEIGHT,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <DialogTitle
                    disableTypography
                    style={{
                        padding: 0,
                        background: `linear-gradient(135deg, ${theme.palette.brand1.main} 0%, #1f4848 100%)`,
                        color: 'white',
                        flexShrink: 0,
                    }}
                >
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                backgroundColor: 'rgba(255,255,255,0.14)',
                                color: 'white',
                                margin: theme.spacing(2, 0, 2, 3),
                            }}
                        >
                            {tab === 'manual' ? (
                                <AssignmentIcon />
                            ) : (
                                <LoriIcon
                                    width={24}
                                    height={24}
                                    color="white"
                                />
                            )}
                        </Container>
                        <Container
                            direction="column"
                            flex={1}
                            style={{ padding: theme.spacing(2, 0) }}
                        >
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    color: 'white',
                                }}
                            >
                                {title}
                            </Container>
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: 'rgba(255,255,255,0.78)',
                                }}
                            >
                                {subtitle}
                            </Container>
                        </Container>
                        <IconButton
                            size="small"
                            onClick={requestClose}
                            style={{
                                color: 'white',
                                marginRight: theme.spacing(2),
                                backgroundColor: 'rgba(255,255,255,0.10)',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Container>
                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        TabIndicatorProps={{
                            style: {
                                height: 3,
                                borderRadius: 999,
                                backgroundColor: 'white',
                            },
                        }}
                        style={{
                            minHeight: 48,
                            paddingLeft: theme.spacing(2),
                            backgroundColor: 'rgba(0,0,0,0.12)',
                        }}
                    >
                        <Tab
                            label="Manual"
                            value="manual"
                            style={{
                                color: 'white',
                                fontWeight: 700,
                                textTransform: 'none',
                                minHeight: 48,
                            }}
                        />
                        <Tab
                            label="Generate with Lori"
                            value="ai"
                            style={{
                                color: 'white',
                                fontWeight: 700,
                                textTransform: 'none',
                                minHeight: 48,
                            }}
                        />
                    </Tabs>
                </DialogTitle>
                <DialogContent
                    style={{
                        padding: theme.spacing(2.5, 3),
                        backgroundColor: '#f6f8f8',
                        overflowY: 'auto',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {tab === 'manual' ? (
                        <Paper
                            elevation={0}
                            style={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 14,
                                overflow: 'hidden',
                                backgroundColor: 'white',
                            }}
                        >
                            <Container
                                direction="column"
                                style={{
                                    padding: theme.spacing(2, 2, 1.5),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: '#fbfcfc',
                                }}
                            >
                                <Container
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight: 700,
                                    }}
                                >
                                    Project Details
                                </Container>
                                <Container
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        color: theme.palette.text.secondary,
                                        marginTop: 2,
                                    }}
                                >
                                    Add the basic scope, schedule and location
                                    for this project.
                                </Container>
                            </Container>
                            <Container
                                direction="column"
                                style={{
                                    gap: theme.spacing(2.5),
                                    padding: theme.spacing(2),
                                }}
                            >
                                {submitError && (
                                    <ErrorBanner message={submitError} />
                                )}
                                <ProjectFieldsEditor
                                    value={manualForm}
                                    onChange={setManualForm}
                                    errors={manualErrors}
                                />
                            </Container>
                        </Paper>
                    ) : (
                        <AiProjectGenerator
                            onCreated={onClose}
                            onDirtyChange={setAiDirty}
                        />
                    )}
                </DialogContent>
                {tab === 'manual' && (
                    <DialogActions
                        style={{
                            padding: theme.spacing(1.5, 3),
                            backgroundColor: '#fafbfb',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            flexShrink: 0,
                        }}
                    >
                        <Container flex={1} />
                        <Button
                            variant="outlined"
                            onClick={requestClose}
                            disabled={submitting}
                            style={{
                                fontWeight: 600,
                                textTransform: 'none',
                                marginRight: theme.spacing(1),
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleManualCreate}
                            disabled={submitting}
                            disableElevation
                            startIcon={<CheckIcon />}
                            style={{
                                fontWeight: 700,
                                textTransform: 'none',
                                minWidth: 160,
                                boxShadow:
                                    '0 1px 2px rgba(70,176,131,0.2), 0 4px 12px rgba(70,176,131,0.15)',
                            }}
                        >
                            Create Project
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
            <Dialog
                open={!!discardConfirm}
                onClose={() => setDiscardConfirm(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                backgroundColor: '#fff4e5',
                                color: '#b26a00',
                                flexShrink: 0,
                            }}
                        >
                            <ErrorOutlineIcon fontSize="small" />
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            {discardConfirm?.title}
                        </Container>
                    </Container>
                </DialogTitle>
                <DialogContent
                    style={{
                        padding: theme.spacing(1.5, 3, 2),
                        color: theme.palette.text.secondary,
                    }}
                >
                    {discardConfirm?.message}
                </DialogContent>
                <DialogActions
                    style={{
                        padding: theme.spacing(1.5, 3, 2.5),
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => setDiscardConfirm(null)}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Keep Editing
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        onClick={() => {
                            const action = discardConfirm?.onConfirm
                            setDiscardConfirm(null)
                            action?.()
                        }}
                        style={{ textTransform: 'none', fontWeight: 700 }}
                    >
                        {discardConfirm?.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
